// src/layouts/WebLayout.jsx
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import MTNLogo from 'assets/mtn-mobile-money.png';
import AirtelLogo from 'assets/airtel-money.png';
import { Heart, Menu, ShoppingCart, X } from 'react-feather';
import { Link } from 'react-router-dom';
import { useAuth } from 'lib/auth';
import { UserNavigation } from './components/UserNavigation';
import { CartSummary } from 'features/account';
import { useHomeData } from 'features/misc/api/getHomeData';

export function WebLayout({ children }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { data: home, isLoading: homeLoading } = useHomeData();

  const { user } = useAuth();

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
  };

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about-us' },
    { name: 'Services', href: '/services' },
    { name: 'Products', href: '/products' },
    { name: 'Impact', href: '/impact' },
    { name: 'Blog', href: '/blog' },
    { name: 'Contact', href: '/contact-us' },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#FFF6F2] text-gray-800">
      {/* ===== Top Bar ===== */}
      <div className="flex items-center justify-between px-6 sm:px-48 py-3">
        <Link to="/" className="flex items-center space-x-3">
          <img src={home?.data?.navbar?.logo} alt="Ayinza Logo" className="h-20 w-auto" />
        </Link>
        <div className="hidden md:flex items-center">
          <Link
            to="/search"
            className="border border-gray-100 rounded-none px-12 py-3 text-sm italic focus:outline-none focus:border-[#AC3F1E]"
          >
            Search...
          </Link>
        </div>
        <button
          className="md:hidden text-gray-800"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {/* ===== Navbar ===== */}
      <nav className=" hidden md:flex items-center justify-between px-6 sm:px-48 py-2">
        <ul className="flex items-center space-x-6 text-md font-medium">
          {navLinks.map((link) => (
            <li key={link.name}>
              <Link
                to={link.href}
                className={`relative hover:text-[#AC3F1E] transition ${
                  window.location.pathname === link.href
                    ? "text-[#AC3F1E] after:content-[''] after:absolute after:left-0 after:-bottom-[6px] after:w-full after:h-[3px] after:bg-[#AC3F1E]"
                    : ''
                }`}
              >
                {link.name}
              </Link>
            </li>
          ))}
        </ul>

        <div className="flex items-center space-x-5">
          {user ? (
            <>
              {/* <Link to="/my-account/wishlist">
                <Heart className="w-5 h-5 hover:text-[#AC3F1E]" />
              </Link> */}

              <CartSummary />
              <UserNavigation />
            </>
          ) : (
            <Link to="/account/login" className="text-sm font-semibold hover:text-[#AC3F1E]">
              Login / Register
            </Link>
          )}
        </div>
      </nav>

      {/* ===== Mobile Menu ===== */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white border-b border-gray-200 md:hidden overflow-hidden"
          >
            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <input
                  type="text"
                  placeholder="Search..."
                  className="w-full border border-gray-400 px-3 py-1 text-sm italic focus:outline-none focus:border-[#AC3F1E]"
                />
              </div>
              <ul className="space-y-3 text-sm font-medium">
                {navLinks.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className="block py-1 hover:text-[#AC3F1E]"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="flex items-center justify-between pt-2">
                <a href="/wishlist">
                  <Heart className="w-5 h-5 hover:text-[#AC3F1E]" />
                </a>

                <CartSummary />
                {user ? (
                  <UserNavigation />
                ) : (
                  <Link to="/account/login" className="text-sm font-semibold hover:text-[#AC3F1E]">
                    Login / Register
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== Main Content ===== */}
      <main className="flex-grow">{children}</main>

      {/* ===== Footer ===== */}
      <footer className=" mt-12">
        <div className="text-center py-10 space-y-6">
          <img src={home?.data?.newsletter?.logo} alt="Ayinza Logo" className="h-32 mx-auto" />
          <p className="max-w-xl mx-auto text-sm px-4">{home?.data?.newsletter?.caption}</p>
          <div className="flex justify-center items-center gap-5 px-4">
            <input
              type="email"
              placeholder="Enter your email"
              className="bg-white border border-gray-400 px-3 py-2 w-64 italic text-gray-700 focus:outline-none focus:border-[#AC3F1E]"
            />
            <button className="bg-[#AC3F1E] text-white uppercase px-5 py-2 text-sm tracking-wide">
              Subscribe
            </button>
          </div>
        </div>

        {/* Map + Overlay Section */}
        <div className="relative mt-12">
          <motion.div
            className="w-full  rounded-lg  shadow-md"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
          >
            <iframe
              title="Ayinza Qreatives Location"
              src={home?.data?.map?.embedUrl}
              className="w-1/2 h-64 md:h-96 mx-auto"
              loading="lazy"
            ></iframe>
          </motion.div>

          <div
            className="bg-cover bg-center py-16 text-white"
            style={{
              backgroundImage: `url(${home?.data?.footer?.backgroundImage})`,
              // marginTop: '-100px',
            }}
          >
            <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 px-6 sm:px-12">
              {home?.data?.footer?.columns?.map((column, colIndex) => (
                <div key={colIndex}>
                  <h4 className="font-bold text-md mb-3">{column?.header}</h4>

                  <ul className="space-y-1 text-sm">
                    {column?.links?.map((link, linkIndex) => (
                      <li key={linkIndex}>
                        <a href={link?.url}>{link?.label}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="flex justify-center mt-10">
              <img
                src={home?.data?.footer?.imageBelowColumns}
                alt="Footer Visual"
                className="h-12 md:h-28"
              />
            </div>
          </div>

          <div className="my-4">
            <div className="text-center text-xs mb-2">Safe and Secure Payments</div>
            <div className="flex flex-row items-center justify-center space-x-4">
              <img src={MTNLogo} alt="MTN Logo" className="h-8" />
              <img src={AirtelLogo} alt="Airtel Logo" className="h-8" />
            </div>
            <div className="text-center text-xs mt-2">
              {home?.data?.footer?.footerCopyRightInfo} Built & maintained by{' '}
              <a href="https://ashita.africa" className="font-bold">
                Ashita Creatives
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
